import "react-pdf/dist/esm/Page/TextLayer.css";

import ErrorState from "components/Error/ErrorState";
import { memo, useEffect, useMemo, useRef, useState } from "react";

import * as Sentry from "@sentry/react";

import PdfPageSkeleton from "./PdfPageSkeleton";

import type {
  PDFDocumentProxy,
  RenderTask,
  TextItem,
} from "pdfjs-dist/types/src/display/api";
import type { MetadocSearchResult } from "services/metadoc-search-api";
import type { PageState } from "../../metadoc.types";

type PdfPageRendererProps = {
  pdfDoc: PDFDocumentProxy | null;
  pageNum: number;
  scale?: number;
  pageState?: PageState;
  onDimensionsCalculated?: (
    pageNum: number,
    width: number,
    height: number,
  ) => void;
  searchHighlights?: MetadocSearchResult[];
};

// renders a single PDF page and its text layer (todo)
const PdfPageRenderer: React.FC<PdfPageRendererProps> = memo(
  ({
    pdfDoc,
    pageNum,
    scale = 1.5,
    onDimensionsCalculated,
    searchHighlights,
  }) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const textLayerRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [renderSize, setRenderSize] = useState<{
      width: number;
      height: number;
    } | null>(null);
    const renderTaskRef = useRef<RenderTask | null>(null);

    useEffect(() => {
      let cancelled = false;

      const renderPage = async () => {
        if (!pdfDoc || !canvasRef.current) return;

        setIsLoading(true);
        setError(null);

        try {
          // cancel any in-flight render
          if (renderTaskRef.current) {
            renderTaskRef.current.cancel();
          }

          const pdfPage = await pdfDoc.getPage(pageNum);

          if (cancelled) return;

          const viewport = pdfPage.getViewport({ scale });
          const canvas = canvasRef.current;
          if (!canvas) return;

          const context = canvas.getContext("2d");
          if (!context) {
            throw new Error("Canvas context not available");
          }

          // set canvas dimensions
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          setRenderSize({ width: viewport.width, height: viewport.height });

          // report dimensions to parent for virtualizer layout BEFORE rendering
          // this prevents the virtualizer from recalculating mid-render
          if (onDimensionsCalculated) {
            const unscaledViewport = pdfPage.getViewport({ scale: 1.0 });
            onDimensionsCalculated(
              pageNum,
              unscaledViewport.width,
              unscaledViewport.height,
            );
          }

          // render PDF page to canvas
          const renderContext = {
            canvasContext: context,
            viewport: viewport,
          };

          renderTaskRef.current = pdfPage.render(renderContext);
          await renderTaskRef.current.promise;
          renderTaskRef.current = null;

          if (cancelled) return;

          // render text layer after canvas completes
          if (textLayerRef.current) {
            textLayerRef.current.innerHTML = ""; // clear previous content

            const textContent = await pdfPage.getTextContent();

            // manually render text layer items with proper positioning
            textContent.items.forEach((item) => {
              const textItem = item as TextItem;
              if (!textItem.str) return;

              const span = document.createElement("span");
              span.textContent = textItem.str;
              span.style.position = "absolute";

              // calculate position and size based on transform matrix
              const tx = textItem.transform;
              const fontSize = Math.sqrt(tx[2] * tx[2] + tx[3] * tx[3]);

              span.style.left = `${tx[4]}px`;
              span.style.top = `${tx[5]}px`;
              span.style.fontSize = `${fontSize}px`;
              span.style.transformOrigin = "0% 0%";

              // apply rotation if needed
              const angle = Math.atan2(tx[1], tx[0]);
              if (angle !== 0) {
                span.style.transform = `rotate(${angle}rad)`;
              }

              textLayerRef.current!.appendChild(span);
            });
          }

          setIsLoading(false);
        } catch (err) {
          const error = err as Error & { name?: string };
          if (!cancelled && error?.name !== "RenderingCancelledException") {
            // log to Sentry for monitoring
            Sentry.captureException(err, {
              tags: { feature: "metadoc", action: "pdf_page_rendering" },
              extra: { pageNum, scale },
            });
            setError(
              err instanceof Error ? err.message : "Failed to render page",
            );
            setIsLoading(false);
          }
        }
      };

      renderPage();

      return () => {
        cancelled = true;
        if (renderTaskRef.current) {
          renderTaskRef.current.cancel();
        }
      };
    }, [pdfDoc, pageNum, scale, onDimensionsCalculated]);

    const highlightShapes = useMemo(() => {
      if (!searchHighlights || searchHighlights.length === 0 || !renderSize) {
        return [];
      }

      return searchHighlights.map((result, index) => {
        const { bounding_box: boundingBox, polygon } = result.geometry;
        if (polygon && polygon.length >= 3) {
          const points = polygon
            .map((point) => {
              const x = point.x * renderSize.width;
              const y = point.y * renderSize.height;
              return `${x},${y}`;
            })
            .join(" ");
          return {
            key: `poly-${index}`,
            type: "polygon",
            points,
          };
        }

        return {
          key: `rect-${index}`,
          type: "rect",
          x: boundingBox.left * renderSize.width,
          y: boundingBox.top * renderSize.height,
          width: boundingBox.width * renderSize.width,
          height: boundingBox.height * renderSize.height,
        };
      });
    }, [renderSize, searchHighlights]);

    if (error) {
      return (
        <div
          className="pdf-page-error"
          style={{
            height: "1200px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <ErrorState
            description={`Failed to load page ${pageNum}: ${error}`}
          />
        </div>
      );
    }

    // calculate expected dimensions for consistent height
    // this ensures the container reserves space even while the page is loading,
    // preventing layout shifts that cause blank areas when scrolling up
    const expectedHeight = (() => {
      if (!pdfDoc) return 800;
      // use scale to calculate expected canvas height
      const viewport = { width: 612, height: 792 }; // default letter size
      return viewport.height * scale;
    })();

    return (
      <div
        ref={containerRef}
        className="pdf-page-container"
        style={{
          position: "relative",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: `${expectedHeight}px`, // reserve space to prevent layout shift
        }}
      >
        {isLoading && (
          <div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "100%",
              maxWidth: "850px",
            }}
          >
            <PdfPageSkeleton />
          </div>
        )}
        <div
          className="pdf-page-layers"
          style={{
            position: "relative",
            display: isLoading ? "none" : "block",
          }}
        >
          {/* layer 1: canvas (pixels) */}
          <canvas
            ref={canvasRef}
            className="pdf-page-canvas"
            style={{
              display: "block",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          />
          {/* layer 2: text layer (selection overlay) */}
          <div
            ref={textLayerRef}
            className="textLayer"
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              right: 0,
              bottom: 0,
              overflow: "hidden",
              opacity: 0.01,
              lineHeight: 1.0,
              pointerEvents: "auto",
              userSelect: "text",
              mixBlendMode: "multiply",
            }}
          />
          {/* layer 3: search highlight overlay */}
          {highlightShapes.length > 0 && renderSize && (
            <svg
              className="metadoc-search-highlights"
              width={renderSize.width}
              height={renderSize.height}
              viewBox={`0 0 ${renderSize.width} ${renderSize.height}`}
              aria-hidden="true"
            >
              {highlightShapes.map((shape) => {
                if (shape.type === "polygon") {
                  return (
                    <polygon
                      key={shape.key}
                      points={shape.points}
                      className="metadoc-search-highlights__shape"
                    />
                  );
                }
                return (
                  <rect
                    key={shape.key}
                    x={shape.x}
                    y={shape.y}
                    width={shape.width}
                    height={shape.height}
                    className="metadoc-search-highlights__shape"
                  />
                );
              })}
            </svg>
          )}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // custom comparison function - only re-render if these props change
    return (
      prevProps.pdfDoc === nextProps.pdfDoc &&
      prevProps.pageNum === nextProps.pageNum &&
      prevProps.scale === nextProps.scale &&
      prevProps.pageState === nextProps.pageState &&
      prevProps.searchHighlights === nextProps.searchHighlights
      // intentionally omit onDimensionsCalculated from comparison to avoid re-renders
    );
  },
);

PdfPageRenderer.displayName = "PdfPageRenderer";

export default PdfPageRenderer;
